import { dropRight } from 'lodash';
import { Editor, Element, Node, Point, Range, Text, Transforms } from 'slate';
import { jsx } from 'slate-hyperscript';
import { ReactEditor } from 'slate-react';

import { ELEMENT_PARAGRAPH } from '../../../slatePlugins';
import { getWrapLinkRange } from '../../DolEditor/elements/hyper-link/transform/getWrapLinkRange';

export function findNodeByElementId({ editor, elementId }) {
  const nodeEntries = Node.nodes(editor) as any;
  for (const [node, path] of nodeEntries) {
    if (node?.id === elementId) {
      return [node as any, path];
    }
  }
  return undefined;
}

export function removeNodeByElementId({ editor, elementId }) {
  const [, path] = findNodeByElementId({ editor, elementId });
  Transforms.setPoint(editor, path, { edge: 'start' });
  Transforms.delete(editor, { at: path });
}

export function removeNodeByElement(editor, element: Element) {
  const path = ReactEditor.findPath(editor, element);

  if (path) {
    Transforms.delete(editor, { at: path });
  }
}

export function clearTextByElementId({ editor, elementId }) {
  const [, path] = findNodeByElementId({ editor, elementId });

  Transforms.insertText(editor, '', {
    at: path,
  });
}

export function isSelectionEndOfEditor({ editor }) {
  let isEnd = false;
  if (editor && editor.selection) {
    const endOfEditorPath = Editor.end(editor, []);
    isEnd = Point.equals(editor.selection.anchor, endOfEditorPath);
  }
  return isEnd;
}

export function getParentNode({ node, path }) {
  let parentNode = null;
  try {
    parentNode = Node.parent(node, path);
    // eslint-disable-next-line no-empty
  } catch (ex) {}

  return parentNode;
}

/**
 * Use to prevent delete start and end of element node
 */

interface CanDeleteParams {
  editor: any;
  unit: 'character' | 'word' | 'line' | 'block' | 'offset' | undefined;
  direction: 'backward' | 'forward';
  match: any;
}

export const canDeleteNode = ({ editor, direction, match }: CanDeleteParams) => {
  const { selection } = editor;
  if (selection && Range.isCollapsed(selection)) {
    let [cell] = Editor.nodes(editor, {
      at: selection,
      match,
    });

    if (!cell) {
      const [pCell] = Editor.nodes(editor, {
        at: selection,
        match: (n: any) => n?.type === ELEMENT_PARAGRAPH,
      });
      if (pCell) {
        const [, paragraphCellPath] = pCell;
        const cellParentPath = dropRight(paragraphCellPath, 1);
        const cellParent = Editor.node(editor, cellParentPath) as any;
        if (match(cellParent?.[0]?.type)) {
          cell = cellParent[0];
        }
      }
    }

    if (cell) {
      const [, cellPath] = cell;

      if (direction === 'backward') {
        const start = Editor.start(editor, cellPath);

        if (Point.equals(selection.anchor, start)) {
          return false;
        }
      }

      if (direction === 'forward') {
        const end = Editor.end(editor, cellPath);

        if (Point.equals(selection.anchor, end)) {
          return false;
        }
      }
    }
  }
  return true;
};

export const getNodeText = (node) => {
  return (node?.children || [])
    .map((n) => {
      if (n?.children) {
        return getNodeText(n);
      }
      return n.text;
    })
    .join('');
};

export const getPrevNode = (editor, path) => {
  const parentPath = dropRight(path, 1);
  const prevPath = [...parentPath, path[path.length - 1] - 1] as any;
  try {
    return Editor.node(editor, prevPath) as any;
    // eslint-disable-next-line no-empty
  } catch (ex) {}
  return null;
};

export const getNextNode = (editor, path) => {
  const parentPath = dropRight(path, 1);
  const nextPath = [...parentPath, path[path.length - 1] + 1] as any;
  try {
    return Editor.node(editor, nextPath) as any;
    // eslint-disable-next-line no-empty
  } catch (ex) {}
  return null;
};

export const toggleInlineTypes = ({
  editor,
  activeType,
  defaultValue = {},
  isCollapseSelection = true,
}) => {
  const { selection } = editor;
  if (!selection) return;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const at = getWrapLinkRange(editor, editor.selection);

  const [node] = Editor.nodes(editor, { at: selection, match: (n: any) => n?.type === activeType });
  const isActive = Boolean(node);

  if (isActive) {
    Transforms.unwrapNodes(editor, {
      match: (n: any) => n?.type === activeType,
    });
  }

  const createNode = {
    type: activeType,
    children: [],
    ...defaultValue,
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, [{ ...createNode, children: [{ text: '' }] }]);
  } else {
    Transforms.wrapNodes(editor, createNode, { at, split: true });
    if (isCollapseSelection) {
      Transforms.collapse(editor, { edge: 'end' });
    }
  }
};

export const deserializeParagraph = (el) => {
  if (el.nodeType === 3) {
    return el.textContent;
  }
  if (el.nodeType !== 1) {
    return null;
  }
  if (el.nodeName === 'BR') {
    return jsx('text', {}, '\n');
  }

  let children = Array.from(el.childNodes).map(deserializeParagraph).flat();

  if (children.length === 0) {
    children = [{ text: '' }] as any;
  }
  if (['H1'].includes(el.nodeName)) {
    return jsx('element', { type: 'H2' }, children);
  }
  if (['H5', 'H6'].includes(el.nodeName)) {
    return jsx('element', { type: 'H4' }, children);
  }
  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children);
  }
  if (el.nodeName === 'P') {
    return jsx('element', { type: 'div' }, children);
  }
  if (el.nodeName === 'DIV') {
    return jsx('element', { type: 'div' }, children);
  }

  return undefined;
};

export const deserializeParagraphWithoutHeading = (el) => {
  if (el.nodeType === 3) {
    return el.textContent;
  }
  if (el.nodeType !== 1) {
    return null;
  }
  if (el.nodeName === 'BR') {
    return jsx('text', {}, '\n');
  }

  let children = Array.from(el.childNodes).map(deserializeParagraphWithoutHeading).flat();

  if (children.length === 0) {
    children = [{ text: '' }] as any;
  }
  if (
    ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DIV', 'P'].some((nodeName) => nodeName === el.nodeName)
  ) {
    return jsx('element', { type: 'div' }, children);
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children);
  }

  return undefined;
};

export const serializeNodeToArray = (node, filterNode: any = () => true) => {
  if (Text.isText(node)) {
    return node.text;
  }
  return node.children.filter(filterNode).map((n) => serializeNodeToArray(n));
};
